import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { submitLead, packFormFields } from '../lib/submitLead';
import { useLeadFormGuard } from '../hooks/useLeadFormGuard';
import { HoneypotField } from '../components/forms/HoneypotField';
import './ContactPage.css';

type RequestType = 'access' | 'docs';
type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  usePageTitle('Contact Us - Get in Touch');

  const typeParam = searchParams.get('type');
  const initialType: RequestType =
    typeParam === 'docs' || typeParam === 'documentation' ? 'docs' : 'access';

  const { honeypot, formProps, getGuardFields } = useLeadFormGuard();

  const [requestType, setRequestType] = useState<RequestType>(initialType);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    useCase: '',
    product: '',
    message: ''
  });
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Update request type when URL changes
  useEffect(() => {
    setRequestType(initialType);
  }, [initialType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState('loading');
    setErrorMessage('');

    // Pack form data into message format
    const packedMessage = packFormFields({
      requestType,
      company: formData.company,
      role: formData.role,
      useCase: formData.useCase,
      product: formData.product,
      message: formData.message
    });

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      message: packedMessage,
      ...getGuardFields()
    });

    if (result.success) {
      setSubmitState('success');
    } else {
      setSubmitState('error');
      setErrorMessage(result.error || 'Failed to submit form. Please try again.');
    }
  };

  if (submitState === 'success') {
    return (
      <div className="contact-page">
        <section className="contact-hero">
          <div className="container">
            <div className="submission-confirmation">
              <div className="confirmation-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1>{t('contact.confirmation.title')}</h1>
              <p>{t('contact.confirmation.message')}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <span className="section-label">{t('contact.label')}</span>
          <h1>{t('contact.title')}</h1>
          <p className="contact-subtitle">{t('contact.subtitle')}</p>

          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-value">{t('contact.heroStats.exposureValue')}</span>
              <span className="hero-stat-label">{t('contact.heroStats.exposureLabel')}</span>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <span className="hero-stat-value">{t('contact.heroStats.firstValue')}</span>
              <span className="hero-stat-label">{t('contact.heroStats.firstLabel')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-form-section section">
        <div className="container">
          {/* Request Type Toggle */}
          <div className="request-type-toggle">
            <button
              type="button"
              className={`toggle-option ${requestType === 'access' ? 'active' : ''}`}
              onClick={() => setRequestType('access')}
            >
              <div className="toggle-icon access-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="toggle-content">
                <span className="toggle-title">{t('contact.types.access.title')}</span>
                <span className="toggle-desc">{t('contact.types.access.desc')}</span>
              </div>
            </button>
            <button
              type="button"
              className={`toggle-option ${requestType === 'docs' ? 'active' : ''}`}
              onClick={() => setRequestType('docs')}
            >
              <div className="toggle-icon docs-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round"/>
                  <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="toggle-content">
                <span className="toggle-title">{t('contact.types.docs.title')}</span>
                <span className="toggle-desc">{t('contact.types.docs.desc')}</span>
              </div>
            </button>
          </div>

          {/* Form Card */}
          <div className="form-card">
            <form className="contact-form" onSubmit={handleSubmit} {...formProps}>
              <HoneypotField {...honeypot} />
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">{t('contact.form.name')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contact.form.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">{t('contact.form.company')}</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.form.companyPlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">{t('contact.form.role')}</label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder={t('contact.form.rolePlaceholder')}
                  />
                </div>
              </div>

              {requestType === 'access' ? (
                <div className="form-group">
                  <label htmlFor="useCase">{t('contact.form.useCase')}</label>
                  <select
                    id="useCase"
                    name="useCase"
                    value={formData.useCase}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact.form.useCasePlaceholder')}</option>
                    <option value="law-firm">{t('contact.form.useCaseOptions.lawFirm')}</option>
                    <option value="consultant">{t('contact.form.useCaseOptions.consultant')}</option>
                    <option value="insurance">{t('contact.form.useCaseOptions.insurance')}</option>
                    <option value="enterprise">{t('contact.form.useCaseOptions.enterprise')}</option>
                    <option value="other">{t('contact.form.useCaseOptions.other')}</option>
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="product">{t('contact.form.product')}</label>
                  <select
                    id="product"
                    name="product"
                    value={formData.product}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t('contact.form.productPlaceholder')}</option>
                    <option value="synthetic-juror">{t('contact.form.productOptions.syntheticJuror')}</option>
                    <option value="fact-finder">{t('contact.form.productOptions.factFinder')}</option>
                    <option value="trial-signal">{t('contact.form.productOptions.trialSignal')}</option>
                    <option value="full-api">{t('contact.form.productOptions.fullApi')}</option>
                  </select>
                </div>
              )}

              <div className="form-group full-width">
                <label htmlFor="message">
                  {requestType === 'access'
                    ? t('contact.form.messageAccess')
                    : t('contact.form.messageDocs')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={requestType === 'access'
                    ? t('contact.form.messageAccessPlaceholder')
                    : t('contact.form.messageDocsPlaceholder')}
                  rows={4}
                />
              </div>

              {submitState === 'error' && (
                <div className="form-error" style={{
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px' }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                className={`btn btn-submit ${requestType === 'docs' ? 'btn-docs' : ''} ${submitState === 'loading' ? 'btn-loading' : ''}`}
                disabled={submitState === 'loading'}
              >
                {submitState === 'loading' ? (
                  <>
                    <span className="btn-spinner" style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      marginRight: '8px'
                    }}></span>
                    Submitting...
                  </>
                ) : (
                  requestType === 'access'
                    ? t('contact.form.submitAccess')
                    : t('contact.form.submitDocs')
                )}
              </button>
            </form>
          </div>

        </div>
      </section>
    </div>
  );
};

export default ContactPage;
